// Cognito authentication wrapper.
//
// We use USER_PASSWORD_AUTH (matching what the legacy Flask app does today)
// against the existing customer Cognito user pool, with no AWS credentials in
// the browser. After a successful login the caller can read the access token
// (NOT the ID token) and use it to call the privacy API behind its
// AVP-backed authorizer.
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from "amazon-cognito-identity-js";
import { CONFIG } from "../generated/config.js";

const userPool = new CognitoUserPool({
  UserPoolId: CONFIG.cognito.user_pool_id,
  ClientId: CONFIG.cognito.app_client_id,
});

let _user = null;
let _accessToken = null;
let _idToken = null;

export function isLoggedIn() {
  return Boolean(_accessToken);
}

export function currentAccessToken() {
  return _accessToken;
}

/** Decode the email claim from the cached ID token. */
export function currentEmail() {
  if (!_idToken) return null;
  try {
    const payload = JSON.parse(atob(_idToken.split(".")[1]));
    return payload.email ?? payload["cognito:username"] ?? null;
  } catch {
    return null;
  }
}

/**
 * Throws on failed authentication. Caller is responsible for catching and
 * rendering a user-facing error message.
 */
export async function login(username, password) {
  const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
  const authDetails = new AuthenticationDetails({
    Username: username,
    Password: password,
  });
  cognitoUser.setAuthenticationFlowType("USER_PASSWORD_AUTH");

  const session = await new Promise((resolve, reject) => {
    cognitoUser.authenticateUser(authDetails, {
      onSuccess: resolve,
      onFailure: reject,
      newPasswordRequired: () =>
        reject(
          new Error(
            "New password required — set the user up in Cognito first.",
          ),
        ),
    });
  });

  _user = cognitoUser;
  _accessToken = session.getAccessToken().getJwtToken();
  _idToken = session.getIdToken().getJwtToken();
}

/**
 * globalSignOut on Cognito (revokes all refresh tokens). Best-effort —
 * we always clear local token state even if the network call fails so a
 * reload can't accidentally resume the session.
 */
export async function logout() {
  const u = _user;
  _user = null;
  _accessToken = null;
  _idToken = null;
  if (!u) return;
  await new Promise((resolve) =>
    u.globalSignOut({ onSuccess: resolve, onFailure: () => resolve() }),
  );
}
